import { useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Icon,
  Paragraph,
  Search,
  Table,
  TableTbody,
  TableTd,
  TableTr,
  Text,
} from '@customerio/pluma-components/react';
import { CIO_OBJECT_ID_KEY } from '@/services/db/object/entity';
import { CREATED_IN_CIO_AT } from '@/utils/attributes';
import { isEmptyString } from '@/utils/string';
import { ObjectAttributeTypeLabel } from './object-attribute-type-label';
import type { AttributeRow } from './use-object-attributes';

const VISIBLE_LIMIT = 100;
const REJECT_ATTRIBUTES: readonly string[] = [CIO_OBJECT_ID_KEY, CREATED_IN_CIO_AT];

type AvailableAttributesProps = {
  rows: readonly AttributeRow[];
  sentAttributes: readonly string[];
  /** The workspace has more attributes than the 2,000 the catalog returns. */
  hasMoreAttributes: boolean;
  isMultiIdentifier: boolean;
  isSaving: boolean;
  onAddAttribute: (attribute: string) => void;
};

export function AvailableAttributes(props: AvailableAttributesProps) {
  const [searchString, setSearchString] = useState('');

  const availableAttributes = useMemo(() => {
    const currentKeys = new Set(props.rows.map((row) => row.key));
    return props.sentAttributes.filter(
      (attribute) => !currentKeys.has(attribute) && !REJECT_ATTRIBUTES.includes(attribute),
    );
  }, [props.rows, props.sentAttributes]);

  const filteredAttributes = useMemo(() => {
    const term = searchString.trim();
    if (isEmptyString(term)) return availableAttributes;
    return availableAttributes.filter((attribute) => attribute.includes(term));
  }, [availableAttributes, searchString]);

  const visibleAttributes = filteredAttributes.slice(0, VISIBLE_LIMIT);
  const allAvailableAttributesMatch = filteredAttributes.length === availableAttributes.length;
  const searchPlaceholder = props.hasMoreAttributes
    ? `Search your first ${(2000).toLocaleString()} attributes`
    : 'Search for attribute';

  return (
    <>
      <Box px="300" pb="300">
        <Search
          data-test-environment-attributes-collection-search
          ariaLabel="Attribute search"
          placeholder={searchPlaceholder}
          value={searchString}
          onSearchInput={setSearchString}
          onSearch={setSearchString}
          shouldSearchOnInput
          isDisabled={props.isSaving}
        />
      </Box>

      {props.rows.length > 0 ? (
        <>
          <Table caption="Available attributes">
            <TableTbody>
              {visibleAttributes.map((attribute) => (
                <TableTr key={attribute} shouldHighlightOnHover={!props.isSaving}>
                  <TableTd
                    role="button"
                    data-test-environment-attributes-collection-row
                    cursor="pointer"
                    onClick={() => {
                      if (!props.isSaving) props.onAddAttribute(attribute);
                    }}
                  >
                    <Flex alignItems="center">
                      <Icon name="add" />
                      <Box as="span" ml="100" data-test-attribute-collection-item-add>
                        <Box as="span" mr="100">
                          {attribute}
                        </Box>
                        <ObjectAttributeTypeLabel attribute={attribute} isMultiIdentifier={props.isMultiIdentifier} />
                      </Box>
                    </Flex>
                  </TableTd>
                </TableTr>
              ))}
            </TableTbody>
          </Table>

          {visibleAttributes.length !== filteredAttributes.length ? (
            <Text as="div" color="subtle" data-test-environment-attributes-collection-not-found>
              Showing {visibleAttributes.length.toLocaleString()} of{' '}
              {props.hasMoreAttributes && allAvailableAttributesMatch
                ? `${filteredAttributes.length.toLocaleString()}+.`
                : `${filteredAttributes.length.toLocaleString()}.`}{' '}
              {!isEmptyString(searchString) ? 'Refine your search.' : 'Start your search.'}
            </Text>
          ) : null}
        </>
      ) : (
        <Paragraph textAlign="center">No attributes available.</Paragraph>
      )}
    </>
  );
}
